import React, { useMemo } from 'react';
import { Page } from '../../layout';
import { Breadcrumb } from '@shared/components/breadcrumb/Breadcrumb';
import useConditionalTabHash from '@shared/hooks/useConditionalTabHash';
import { useTabExistCheck } from '@shared/hooks/useTabExistCheck';
import { getActiveTabDisplayName } from '@shared/services/getActiveTabDisplayNameService';
import ResponsiveTabMenu from '@shared/components/ResponsiveTabMenu';
import ExecutiveSummaryReport from './ExecutiveSummaryReport';
import { useQuery, useReactiveVar } from '@apollo/client';
import { userInfo } from '@apollo-internal/cache';
import { getExecutiveSummaryUrl } from '@shared/services/getUnitSearchPageUrlService';
import OrgStrengthsAndChallenges from './OrgStrengthsAndChallenges';
import PrivilegeNames from '../../../constants/privilegeNames';
import ApolloError from '@shared/components/ApolloError';
import GET_ORGS_AND_UNITS_WITH_APPROVED_MDDAP_APPRAISAL from '../../dataDownload/queries/getOrgsAndUnitWithApprovedMddapAppraisalQuery';
import MotionSection from '@shared/components/animations/MotionSection';

const PAGE_TITLE = 'Executive Summary';

const TAB_EXECUTIVE_SUMMARY_TITLE = 'Executive Summary';
const TAB_STRENGTHS_AND_CHALLENGES_TITLE = 'Strengths and Challenges';

const TAB_VALUES = {
  ExecutiveSummary: 'executive-summary',
  StrengthAndChallenges: 'strengths-challenges'
};

const TAB_COMPONENT_MAP = {
  [TAB_VALUES.ExecutiveSummary]: ExecutiveSummaryReport,
  [TAB_VALUES.StrengthAndChallenges]: OrgStrengthsAndChallenges
};

/**
 * @typedef {object} ExecutiveSummaryPageProps
 * @property {number} [orgId]
 */

/**
 * @param {ExecutiveSummaryPageProps} props
 */
const ExecutiveSummaryPage = props => {
  const { orgId } = props;

  const currentUserInfo = useReactiveVar(userInfo);

  const { data, error } = useQuery(GET_ORGS_AND_UNITS_WITH_APPROVED_MDDAP_APPRAISAL, {
    variables: { orgId: orgId }
  });

  const isOrgHasMultipleActiveAppraisedOrgUnits = isOrgHasMultipleApprovedUnits(data, orgId);
  const tabs = useMemo(
    () => getTabs(currentUserInfo.Privileges, isOrgHasMultipleActiveAppraisedOrgUnits),
    [currentUserInfo.Privileges, isOrgHasMultipleActiveAppraisedOrgUnits]
  );
  const { activeTab, setActiveTab, hasMultipleTabs } = useConditionalTabHash(tabs);
  useTabExistCheck(tabs, activeTab);

  const activeTabDisplayName = getActiveTabDisplayName(tabs, activeTab);
  const ActiveTabComponent = TAB_COMPONENT_MAP[activeTab];

  const breadcrumbPathItems = [
    { name: 'Data Reports', url: '/data-reports' },
    { name: 'Organization Reports', url: '/data-reports/organization-reports' },
    {
      name: 'Executive Summary',
      url: getExecutiveSummaryUrl(currentUserInfo.Privileges)
    },
    { name: activeTabDisplayName, url: '' }
  ];

  const handleTabChange = type => {
    setActiveTab(type);
  };

  if (error) {
    return <ApolloError />;
  }

  return (
    <Page title={PAGE_TITLE} className='executive-summary-page'>
      <Breadcrumb pathItems={breadcrumbPathItems} />
      <MotionSection>
        {hasMultipleTabs && (
          <ResponsiveTabMenu activeTab={activeTab} tabs={tabs} onChange={handleTabChange} />
        )}
      </MotionSection>
      <ActiveTabComponent orgId={orgId} />
    </Page>
  );
};

const getTabs = (privileges, isOrgHasMultipleActiveAppraisedOrgUnits) => {
  const tabs = [{ value: TAB_VALUES.ExecutiveSummary, displayName: TAB_EXECUTIVE_SUMMARY_TITLE }];

  if (
    privileges.includes(
      PrivilegeNames.OrganizationReports_ExecutiveSummary_OrgStrengthsAndChallenges
    ) &&
    isOrgHasMultipleActiveAppraisedOrgUnits
  ) {
    tabs.push({
      value: TAB_VALUES.StrengthAndChallenges,
      displayName: TAB_STRENGTHS_AND_CHALLENGES_TITLE
    });
  }

  return tabs;
};

const isOrgHasMultipleApprovedUnits = (data, orgId) => {
  const matchedOrg = data?.orgsAndUnitsWithApprovedMddapAppraisal?.find(org => org.Id === orgId);

  return matchedOrg?.MddapOrgUnits?.length > 1;
};

export default ExecutiveSummaryPage;
